import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import { useAppSelector } from 'src/services/redux/app/store';
import * as Yup from 'yup';
import { useCustomerVisitCreate, useSalesStaffList } from '../service';
import { fetchAllCustomersForSelectAsync } from '../../customers/service';
import PotentialCustomerCreate from '../../customers/modals/potential-customer-create';
import BasicInfoSurveyModal from '../../customers/modals/basic-info-survey-modal';
import SalesOpportunityCreate from '../../sales-opportunities/modals/record-create';

type Props = {
  show: boolean;
  handleClose: Function;
  refetchList: Function;
  preselectedCustomer?: {
    id: number;
    name: string;
    type: 'potential' | 'erp';
  } | null;
};

const validationSchema = Yup.object().shape({
  customer_id: Yup.string().required('Müşteri seçimi zorunludur'),
  meeting_date: Yup.string().required('Toplantı tarihi zorunludur'),
  meeting_location: Yup.string().required('Toplantı lokasyonu zorunludur'),
});

const meetingLocations = [
  { value: 'Online', label: 'Online' },
  { value: 'Müşteri Yerine', label: 'Müşteri Yerine' },
  { value: 'Aynet Ofisi', label: 'Aynet Ofisi' },
  { value: 'Diğer', label: 'Diğer' },
];

// Custom format for option label with badge
const formatOptionLabel = (option: any, { context }: any) => (
  <div className="d-flex align-items-center justify-content-between">
    <span>{option.label}</span>
    {context === 'menu' && (
      <div>
        {option.isPotential && <span className="badge badge-light-warning ms-2 fs-9">Potansiyel</span>}
        {option.isErp && <span className="badge badge-light-primary ms-2 fs-9">DIA</span>}
      </div>
    )}
    {context === 'value' && (
      <div>
        {option.isPotential && <span className="badge badge-light-warning ms-2 fs-9">Potansiyel</span>}
        {option.isErp && <span className="badge badge-light-primary ms-2 fs-9">DIA</span>}
      </div>
    )}
  </div>
);

// Select stilleri
const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: '#f5f8fa',
    borderColor: state.isFocused ? '#009ef7' : '#e4e6ef',
    boxShadow: state.isFocused ? '0 0 0 1px #009ef7' : 'none',
    '&:hover': {
      borderColor: '#009ef7',
    },
    minHeight: '42px',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? '#009ef7' : state.isFocused ? '#f1faff' : 'white',
    color: state.isSelected ? 'white' : '#181c32',
    cursor: 'pointer',
    padding: '10px 12px',
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    boxShadow: '0 0 50px 0 rgba(82,63,105,.15)',
    borderRadius: '0.475rem',
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: '280px',
  }),
};

export default function VisitCreate({ show, handleClose, refetchList, preselectedCustomer }: Props) {
  const { user } = useAppSelector((state) => state.auth);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [selectedSalesStaff, setSelectedSalesStaff] = useState<any>(null);
  const [defaultOptions, setDefaultOptions] = useState<any[]>([]);
  const [potentialCustomerModal, setPotentialCustomerModal] = useState(false);
  const [surveyModal, setSurveyModal] = useState(false);

  // Satış fırsatı için state'ler
  const [showSalesOpportunityModal, setShowSalesOpportunityModal] = useState(false);
  const [savedCustomerForOpportunity, setSavedCustomerForOpportunity] = useState<any>(null);
  const [addSalesOpportunity, setAddSalesOpportunity] = useState(false);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const onSuccess = () => {
    toast.success('Ziyaret kaydı başarıyla oluşturuldu');
    // Satış fırsatı modalı gösterilecekse ana modalı kapatmadan bekle
    if (savedCustomerForOpportunity) {
      setShowSalesOpportunityModal(true);
    } else {
      handleClose();
    }
    refetchList();
  };

  const onError = (e: any) => {
    e?.response?.data?.message && toast.error(e?.response?.data?.message);
  };

  const { mutate, isLoading } = useCustomerVisitCreate(onSuccess, onError);
  const { data: salesStaffData } = useSalesStaffList(onError);

  // Müşterileri yükle
  const loadCustomers = async (search: string = ''): Promise<any[]> => {
    try {
      const data = await fetchAllCustomersForSelectAsync({
        sales_staff: user?.id,
        sales_staff_key: user?.sales_staff_key,
        session_id: user?.dia_session_id,
        search,
        user_id: user?.id,
      });

      const formattedList = (data?.all || []).map((c: any) => ({
        value: c.type === 'potential' ? `potential_${c.id}` : c.id,
        label: c.name,
        isPotential: c.type === 'potential',
        isErp: c.type === 'erp',
        type: c.type,
        originalId: c.id,
        carikartkodu: c.carikartkodu,
        has_survey: c.has_survey,
      }));

      return formattedList;
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
      return [];
    }
  };

  // Debounced load options for AsyncSelect
  const loadOptions = (inputValue: string): Promise<any[]> => {
    return new Promise((resolve) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        const options = await loadCustomers(inputValue);
        resolve(options);
      }, 400);
    });
  };

  // Modal açıldığında default options yükle
  useEffect(() => {
    if (show) {
      // Preselected customer varsa onu seç
      if (preselectedCustomer) {
        const formattedCustomer = {
          value: preselectedCustomer.type === 'potential' ? `potential_${preselectedCustomer.id}` : preselectedCustomer.id,
          label: preselectedCustomer.name,
          isPotential: preselectedCustomer.type === 'potential',
          isErp: preselectedCustomer.type === 'erp',
          type: preselectedCustomer.type,
          originalId: preselectedCustomer.id,
        };
        setSelectedCustomer(formattedCustomer);
      } else {
        setSelectedCustomer(null);
      }
      setSelectedLocation(null);
      loadCustomers('').then((options) => {
        setDefaultOptions(options);
      });
    }
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [show, preselectedCustomer]);

  // Satış personeli listesini formatla
  const salesStaffList = useMemo(() => {
    return (
      salesStaffData?.results?.map((e: any) => ({
        value: e.id,
        label: `${e.name} ${e.surname}`,
      })) || []
    );
  }, [salesStaffData]);

  // Varsayılan satış personelini seç
  useEffect(() => {
    if (user?.is_sales && salesStaffList.length > 0) {
      const currentUser = salesStaffList.find((s: any) => s.value === user.id);
      setSelectedSalesStaff(currentUser || null);
    }
  }, [user, salesStaffList]);

  // Bekleyen form değerleri için ref
  const pendingFormValuesRef = useRef<any>(null);

  const handleSubmit = (values: any) => {
    // Eğer satış fırsatı eklenecekse müşteri bilgisini sakla
    if (addSalesOpportunity && selectedCustomer) {
      setSavedCustomerForOpportunity({
        id: selectedCustomer.originalId || selectedCustomer.value,
        name: selectedCustomer.label,
        type: selectedCustomer.type,
      });
    } else {
      setSavedCustomerForOpportunity(null);
    }

    mutate({
      customer_id: values.customer_id,
      customer_type: values.customer_type,
      customer_name: values.customer_name,
      meeting_date: values.meeting_date,
      sales_staff: values.sales_staff || user?.id,
      meeting_location: values.meeting_location,
      notes: values.notes,
    });
  };

  return (
    <Modal show={show} onHide={() => handleClose()} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Yeni Müşteri Ziyareti</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={{
          customer_id: '',
          customer_type: '',
          customer_name: '',
          meeting_date: '',
          sales_staff: user?.is_sales ? user?.id : '',
          meeting_location: '',
          notes: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue }) => (
          <Form>
            <Modal.Body>
              {/* Müşteri Seçimi */}
              <div className="mb-5">
                <label className="form-label required fw-bold">Müşteri</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={defaultOptions}
                  loadOptions={loadOptions}
                  value={selectedCustomer}
                  onChange={(option: any) => {
                    setSelectedCustomer(option);
                    setFieldValue('customer_id', option?.originalId || option?.value || '');
                    setFieldValue('customer_type', option?.type || '');
                    setFieldValue('customer_name', option?.label || '');
                  }}
                  placeholder="Müşteri ara veya seç..."
                  isClearable
                  loadingMessage={() => 'Aranıyor...'}
                  noOptionsMessage={() => (
                    <div className="text-center py-2">
                      <div className="text-muted mb-2">Müşteri bulunamadı</div>
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPotentialCustomerModal(true);
                        }}
                      >
                        <i className="bi bi-plus-lg me-1"></i>
                        Potansiyel Müşteri Ekle
                      </button>
                    </div>
                  )}
                  formatOptionLabel={formatOptionLabel}
                  styles={selectStyles}
                  className={errors.customer_id && touched.customer_id ? 'is-invalid' : ''}
                />
                {errors.customer_id && touched.customer_id && <div className="text-danger fs-7 mt-1">{errors.customer_id}</div>}
              </div>
              {selectedCustomer && (selectedCustomer.isPotential || selectedCustomer.isErp) && selectedCustomer.has_survey === false && (
                <div className="alert alert-warning d-flex align-items-center p-4 mb-5">
                  <i className="bi bi-exclamation-triangle-fill text-warning fs-3 me-3"></i>
                  <div className="d-flex flex-column flex-grow-1">
                    <span className="fw-bold">Bu müşterinin temel bilgileri henüz doldurulmamış.</span>
                    <span className="text-muted fs-7">Müşteri hakkında daha detaylı bilgi edinmek için temel bilgi anketini doldurabilirsiniz.</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-warning ms-3" onClick={() => setSurveyModal(true)}>
                    <i className="bi bi-clipboard-check me-1"></i>
                    Temel Bilgileri Doldur
                  </button>
                </div>
              )}

              {/* Toplantı Tarihi */}
              {/* <div className="mb-5">
                <label className="form-label required fw-bold">Toplantı Tarihi</label>
                <Field type="date" name="meeting_date" className={`form-control ${errors.meeting_date && touched.meeting_date ? 'is-invalid' : ''}`} />
                {errors.meeting_date && touched.meeting_date && <div className="invalid-feedback">{errors.meeting_date}</div>}
              </div> */}

              <div className="row">
                {/* Satış Personeli */}
                <div className="d-none col-md-6 mb-5 d-none">
                  <label className="form-label fw-bold">Satış Personeli</label>
                  <Select
                    options={salesStaffList}
                    value={selectedSalesStaff}
                    onChange={(option: any) => {
                      setSelectedSalesStaff(option);
                      setFieldValue('sales_staff', option?.value || '');
                    }}
                    placeholder="Satış personeli seçiniz..."
                    isClearable
                    isDisabled={user?.is_sales === true}
                  />
                  {user?.is_sales && <div className="text-muted fs-8 mt-1">Satış personeli olarak sadece kendi adınıza ziyaret ekleyebilirsiniz.</div>}
                </div>

                {/* Toplantı Tarihi */}
                <div className="col-md-6 mb-5">
                  <label className="form-label required fw-bold">Toplantı Tarihi</label>
                  <Field
                    type="date"
                    name="meeting_date"
                    className={`form-control ${errors.meeting_date && touched.meeting_date ? 'is-invalid' : ''}`}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => e.currentTarget.showPicker()}
                  />
                  {errors.meeting_date && touched.meeting_date && <div className="invalid-feedback">{errors.meeting_date}</div>}
                </div>
                {/* Toplantı Lokasyonu */}
                <div className="col-md-6 mb-5">
                  <label className="form-label required fw-bold">Toplantı Lokasyonu</label>
                  <Select
                    options={meetingLocations}
                    value={selectedLocation}
                    onChange={(option: any) => {
                      setSelectedLocation(option);
                      setFieldValue('meeting_location', option?.value || '');
                    }}
                    placeholder="Lokasyon seçiniz..."
                    isClearable
                    styles={selectStyles}
                    className={errors.meeting_location && touched.meeting_location ? 'is-invalid' : ''}
                  />
                  {errors.meeting_location && touched.meeting_location && <div className="text-danger fs-7 mt-1">{errors.meeting_location}</div>}
                </div>
              </div>

              {/* Notlar */}
              <div className="mb-5">
                <label className="form-label fw-bold">Notlar</label>
                <Field as="textarea" name="notes" rows={4} className="form-control" placeholder="Toplantı notları..." />
              </div>

              {/* Satış Fırsatı Checkbox */}
              <div className="mb-3">
                <div className="form-check form-check-custom form-check-solid form-check-sm">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="addSalesOpportunity"
                    checked={addSalesOpportunity}
                    onChange={(e) => setAddSalesOpportunity(e.target.checked)}
                  />
                  <label className="form-check-label text-gray-700" htmlFor="addSalesOpportunity">
                    Bu bir satış fırsatıdır
                  </label>
                </div>
                {addSalesOpportunity && (
                  <div className="text-muted fs-8 mt-2 ms-6">
                    <i className="bi bi-info-circle me-1"></i>
                    Ziyaret kaydedildikten sonra satış fırsatı ekleme penceresi açılacaktır.
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-light btn-sm" onClick={() => handleClose()} disabled={isLoading}>
                İptal
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={isLoading}>
                {isLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                Oluştur
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>

      {/* Potansiyel Müşteri Ekleme Modal */}
      <PotentialCustomerCreate
        show={potentialCustomerModal}
        handleClose={() => setPotentialCustomerModal(false)}
        refetchList={() => {
          // Modal kapandıktan sonra müşteri listesini yenile
          loadCustomers('').then((options) => {
            setDefaultOptions(options);
          });
        }}
      />

      {/* Temel Bilgi Anketi Modal */}
      {selectedCustomer && (selectedCustomer.isPotential || selectedCustomer.isErp) && (
        <BasicInfoSurveyModal
          show={surveyModal}
          handleClose={() => {
            setSurveyModal(false);
            // Anketi doldurduktan sonra müşteri listesini yenileyerek has_survey güncelle
            loadCustomers('').then((options) => {
              setDefaultOptions(options);
              const updatedCustomer = options.find((o: any) => o.value === selectedCustomer.value);
              if (updatedCustomer) setSelectedCustomer(updatedCustomer);
            });
          }}
          potentialCustomer={{ id: selectedCustomer.originalId, name: selectedCustomer.label }}
          customerType={selectedCustomer.isErp ? 'erp' : 'potential'}
        />
      )}

      {/* Satış Fırsatı Ekleme Modalı */}
      <SalesOpportunityCreate
        show={showSalesOpportunityModal}
        handleClose={() => {
          setShowSalesOpportunityModal(false);
          setSavedCustomerForOpportunity(null);
          setAddSalesOpportunity(false);
          handleClose();
        }}
        refetchList={() => {}}
        preselectedCustomer={savedCustomerForOpportunity}
      />
    </Modal>
  );
}
